import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../utils/API";
import "./orgIssueProfile.css";
import RulemakingBillList from "./RulemakingBillList";

const POSITION_LABELS = {
    support: "Support",
    support_if_amended: "Support if amended",
    oppose: "Oppose",
    oppose_unless_amended: "Oppose unless amended",
    neutral: "Neutral",
};

function formatSession(session) {
    return `${session.slice(0, 4)}–${session.slice(4)}`;
}

function Card({ icon, title, note, children, className = "" }) {
    return (
        <div className={`issue-card ${className}`}>
            <div className="issue-card-header">
                <i className={`bi ${icon}`}></i>
                <h6>{title}</h6>
            </div>
            {note && <p className="issue-card-note">{note}</p>}
            <div className="issue-card-body">{children}</div>
        </div>
    );
}

// Green/red split with the raw counts beside it, so a row reads "mostly opposed" at a glance.
function StanceBar({ support, oppose }) {
    const total = support + oppose;
    if (total === 0) return null;
    return (
        <span className="issue-stance-bar" title={`${support} support, ${oppose} oppose`}>
            <span className="issue-bar-track">
                <span className="issue-bar-support" style={{ width: `${(support / total) * 100}%` }} />
            </span>
            <span className="issue-bar-counts">
                <span className="issue-count-support">{support}</span>
                <span className="issue-count-sep">/</span>
                <span className="issue-count-oppose">{oppose}</span>
            </span>
        </span>
    );
}

function MetricRow({ label, sub, support, oppose, to }) {
    return (
        <div className="issue-row">
            <div className="issue-row-main">
                <div className="issue-row-label">{to ? <Link to={to}>{label}</Link> : label}</div>
                {sub && <div className="issue-row-sub">{sub}</div>}
            </div>
            <StanceBar support={support} oppose={oppose} />
        </div>
    );
}

function StanceBadge({ stance, position }) {
    return <span className={`issue-badge issue-badge-${position}`}>{POSITION_LABELS[stance] || stance}</span>;
}

function SessionGroup({ session, bills, defaultOpen }) {
    const [open, setOpen] = useState(defaultOpen);
    const [quoted, setQuoted] = useState(null);
    const support = bills.filter(b => b.position === "support").length;
    const oppose = bills.filter(b => b.position === "oppose").length;

    return (
        <div className="issue-session">
            <button type="button" className="issue-session-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
                <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"}`}></i>
                <span className="issue-session-name">{formatSession(session)}</span>
                <span className="issue-session-count">{bills.length} bills</span>
                <StanceBar support={support} oppose={oppose} />
            </button>
            {open && bills.map(bill => (
                <div className="issue-bill" key={bill.bill_id}>
                    <div className="issue-bill-line">
                        <Link className="issue-bill-id" to={`/legislation/bill/${bill.bill_id}`}>{bill.display}</Link>
                        <span className="issue-bill-title" title={bill.title}>{bill.title}</span>
                        <StanceBadge stance={bill.stance} position={bill.position} />
                        {bill.from_workspace && <span className="issue-bill-status" title="Position recorded in your Workspace">Workspace</span>}
                        <span className="issue-bill-status">{bill.status}</span>
                        {bill.evidence && (
                            <button type="button" className="issue-quote-btn" onClick={() => setQuoted(quoted === bill.bill_id ? null : bill.bill_id)} title="Show quote">
                                <i className="bi bi-chat-quote"></i>
                            </button>
                        )}
                    </div>
                    {bill.changed && <div className="issue-bill-flag">Position differed between analyses</div>}
                    {quoted === bill.bill_id && <blockquote className="issue-evidence">{bill.evidence}</blockquote>}
                </div>
            ))}
        </div>
    );
}

export default function OrgIssueProfile({ organization_id }) {
    const [profile, setProfile] = useState(null);
    const [emerging, setEmerging] = useState([]);
    const [rulemaking, setRulemaking] = useState([]);
    const [noticesAvailable, setNoticesAvailable] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [stanceFilter, setStanceFilter] = useState("all");

    useEffect(() => {
        if (!organization_id) return;
        let cancelled = false;
        setLoading(true);
        setError(false);
        // The profile is required; the emerging and rulemaking lists are extras that should not blank the tab.
        const optional = request => request.then(res => res.data).catch(() => null);
        Promise.all([
            API.getOrgIssueProfile(organization_id),
            optional(API.getOrgEmergingBills(organization_id)),
            optional(API.getOrgRulemaking(organization_id)),
        ])
            .then(([profileRes, emergingData, rulemakingData]) => {
                if (cancelled) return;
                setProfile(profileRes.data);
                setEmerging(emergingData?.bills || []);
                setRulemaking(rulemakingData?.bills || []);
                setNoticesAvailable(rulemakingData?.notices_available !== false);
            })
            .catch(() => !cancelled && setError(true))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [organization_id]);

    if (loading) {
        return (
            <div className="issue-profile-status">
                <div className="spinner-border loading-spinner" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }
    if (error) return <p className="issue-profile-status">Unable to load the issue profile.</p>;

    if (!profile || profile.totals.bills === 0) {
        return (
            <div className="issue-profile">
                <p className="issue-profile-status">
                    No recorded positions for this organization. Positions are read from committee and floor analyses that name the organization in a support or opposition list.
                </p>
            </div>
        );
    }

    const { totals, themes, sessions } = profile;
    const stats = [
        { label: "Bills", value: totals.bills },
        { label: "Support", value: totals.support, tone: "support" },
        { label: "Oppose", value: totals.oppose, tone: "oppose" },
        ...(totals.neutral > 0 ? [{ label: "Neutral", value: totals.neutral }] : []),
        { label: "Sessions", value: totals.sessions },
    ];
    const visibleSessions = sessions
        .map(s => ({ ...s, bills: s.bills.filter(b => stanceFilter === "all" || b.position === stanceFilter) }))
        .filter(s => s.bills.length > 0);

    return (
        <div className="issue-profile">
            <div className="issue-stats">
                {stats.map(stat => (
                    <div className={`issue-stat ${stat.tone ? `issue-stat-${stat.tone}` : ""}`} key={stat.label}>
                        <div className="issue-stat-value">{stat.value}</div>
                        <div className="issue-stat-label">{stat.label}</div>
                    </div>
                ))}
            </div>
            <p className="issue-note">
                Positions are inferred from bill analyses that list or quote this organization
                {profile.aliases.length > 0 && <> (matched on {profile.aliases.join("; ")})</>}. A research aid, not an official record.
                Counts read support / oppose.
                {profile.includes_workspace && <> Counts include positions recorded in your Workspace, which other users do not see.</>}
            </p>

            <div className="issue-grid">
                {emerging.length > 0 && (
                    <Card icon="bi-binoculars-fill" title="Similar Bills Now" note="Current-session bills touching the same code sections and topics" className="issue-card-wide">
                        {emerging.slice(0, 10).map(bill => (
                            <div className="issue-row" key={bill.bill_id}>
                                <div className="issue-row-main">
                                    <div className="issue-row-label">
                                        <Link to={`/legislation/bill/${bill.bill_id}`}>{bill.display}</Link>
                                        <span className="issue-row-title"> {bill.title}</span>
                                    </div>
                                    <div className="issue-row-sub">{bill.reasons.slice(0, 3).map(r => r.label).join(" · ")}</div>
                                </div>
                                {bill.likely_position && <span className={`issue-badge issue-badge-${bill.likely_position}`}>Past: {bill.likely_position}</span>}
                            </div>
                        ))}
                    </Card>
                )}

                {themes.codes.length > 0 && (
                    <Card icon="bi-journal-text" title="Codes Amended">
                        {themes.codes.map(c => (
                            <MetricRow key={c.id} label={c.code_title || c.code} sub={`${c.code} · ${c.bills} bills`} support={c.support} oppose={c.oppose} />
                        ))}
                    </Card>
                )}

                {themes.sections.length > 0 && (
                    <Card icon="bi-bookmark-fill" title="Recurring Sections" note="Specific statutes these bills repeatedly amend">
                        {themes.sections.map(s => (
                            <MetricRow key={s.id} label={`${s.code} §${s.section}`} sub={`${s.bills} bills`} support={s.support} oppose={s.oppose} />
                        ))}
                    </Card>
                )}

                {themes.topics.length > 0 && (
                    <Card icon="bi-tags-fill" title="Topics">
                        {themes.topics.map(t => (
                            <MetricRow key={t.id} label={t.topic} sub={`${t.bills} bills`} support={t.support} oppose={t.oppose} />
                        ))}
                    </Card>
                )}

                {rulemaking.length > 0 && (
                    <Card icon="bi-hammer" title="Rulemaking Ahead" note={`Recently enacted bills still moving through rulemaking, from the bill digest and proposed regulations in the OAL Notice Register.${noticesAvailable ? "" : " Notice Register data has not been loaded yet, so hearing dates are not shown."}`} className="issue-card-wide">
                        <RulemakingBillList bills={rulemaking} renderBadge={bill => <span className={`issue-badge issue-badge-${bill.position}`}>{bill.position === "support" ? "Support" : "Oppose"}</span>} />
                    </Card>
                )}

                <Card icon="bi-clock-history" title="Positions by Session" className="issue-card-wide">
                    <div className="issue-filter-row">
                        <select className="form-select form-select-sm issue-filter" value={stanceFilter} onChange={e => setStanceFilter(e.target.value)}>
                            <option value="all">All positions</option>
                            <option value="support">Support</option>
                            <option value="oppose">Oppose</option>
                            <option value="neutral">Neutral</option>
                        </select>
                        {themes.bills_with_code_sections < totals.bills && (
                            <span className="issue-note">Themes cover {themes.bills_with_code_sections} of {totals.bills} bills</span>
                        )}
                    </div>
                    {visibleSessions.map((s, i) => (
                        <SessionGroup key={`${s.session}-${stanceFilter}`} session={s.session} bills={s.bills} defaultOpen={i === 0} />
                    ))}
                </Card>
            </div>
        </div>
    );
}
